import Phaser from 'phaser'
import { gameBus } from '../../bus/GameEventBus'
import { audio } from '../JKGame_audio'
import { boonDesc, boonName, partLabel, t, weaponName } from '../../i18n'
import { getActiveChar } from '../../services/JKGame_loadout'
import { matchController, type PlayerView, type TargetView } from '../../services/JKGame_MatchController'
import { RAGE_SPECIAL } from '../../sim/JKGame_match'
import { ARCH_LABEL, ARCH_TINT, type Emotion, porKey, portraitSetFor } from '../JKGame_assets'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../JKGame_dims'
import { COLORS, FONT, hasFinePointer, hpColor, hpColorHex, makeButton, prefersReducedMotion } from '../JKGame_ui'

interface CombatData {
  targetId: string
}

export class JKGame_CombatScene extends Phaser.Scene {
  private targetId = ''
  private ended = false
  private fleeing = false // true once a flee is submitted (prevents duplicate backend flees)
  private prevEnemyHp = 0
  private prevPlayerHp = 0
  private portraitSet = 'fem_1'
  private disposers: Array<() => void> = []

  private portrait!: Phaser.GameObjects.Image
  private enemyName!: Phaser.GameObjects.Text
  private enemyHpFill!: Phaser.GameObjects.Rectangle
  private enemyHpTxt!: Phaser.GameObjects.Text
  private hpFill!: Phaser.GameObjects.Rectangle
  private spFill!: Phaser.GameObjects.Rectangle
  private rageFill!: Phaser.GameObjects.Rectangle
  private rageLabel!: Phaser.GameObjects.Text // "怒氣 N / 100" — numeric parity with the HP/SP readouts
  private statusTxt!: Phaser.GameObjects.Text
  private hpValTxt!: Phaser.GameObjects.Text
  private spValTxt!: Phaser.GameObjects.Text
  private injuryTxt!: Phaser.GameObjects.Text
  private logTxt!: Phaser.GameObjects.Text
  private buttons: Phaser.GameObjects.Container[] = []
  private specialTween?: Phaser.Tweens.Tween
  private pendingSpecialFx = false // set when a special is unleashed; gilds the next damage number
  private pendingAttackFx = false // set when the player attacks; a no-damage result floats "MISS"
  private hitTween?: Phaser.Tweens.Tween
  private enemyHpTween?: Phaser.Tweens.Tween // smooth enemy HP-bar drain
  private playerHpTween?: Phaser.Tweens.Tween // smooth player HP-bar drain
  private rageTween?: Phaser.Tweens.Tween // smooth rage-bar charge
  private lowHpTween?: Phaser.Tweens.Tween // critical-HP danger pulse (player)
  private enemyLowTween?: Phaser.Tweens.Tween // critical-HP "finish them" pulse (enemy)
  private portraitHomeX = 0 // portrait's resting x; shakes are relative to this (no drift)
  private builtCanSpecial?: boolean // last-built button layout state (avoid needless rebuilds)
  private builtWeapon?: string
  private weaponFlashTween?: Phaser.Tweens.Tween // brief flash when the weapon changes (e.g. ammo → fists)

  constructor() {
    super('Combat')
  }

  create(data: CombatData): void {
    this.ended = false
    this.fleeing = false
    this.pendingSpecialFx = false
    this.targetId = data.targetId
    const target = this.target()
    const p = this.player()
    if (!target || !target.alive) {
      this.endCombat()
      return
    }
    this.prevEnemyHp = target.hp
    this.prevPlayerHp = p.hp
    this.portraitSet = portraitSetFor(target.id)

    this.cameras.main.fadeIn(220, 0, 0, 0) // quick reveal on engaging combat
    this.drawCorridor()
    this.buildUi(target)
    this.render()

    this.disposers.push(gameBus.on('match:updated', () => this.onUpdated()))
    this.disposers.push(gameBus.on('match:over', () => this.onOver()))

    // Desktop combat keys mirror the on-screen buttons (the action methods guard
    // `ended`; an unready special is harmlessly rejected by the sim with feedback).
    const kb = this.input.keyboard
    kb?.on('keydown-SPACE', () => this.attack())
    kb?.on('keydown-S', () => this.special())
    kb?.on('keydown-ESC', () => this.flee())
    // Desktop-only hint for the (non-obvious) combat keys; hidden on touch.
    if (hasFinePointer()) {
      this.add.text(DESIGN_WIDTH / 2, 16, t('combat.kbd'), { fontFamily: FONT, fontSize: '12px', color: COLORS.sub }).setOrigin(0.5, 0)
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup())
  }

  private player(): PlayerView {
    return matchController.getPlayerView()
  }
  private target(): TargetView | null {
    return matchController.getTargetView(this.targetId)
  }

  // ─── visuals ────────────────────────────────────────────────────────────────

  private drawCorridor(): void {
    const g = this.add.graphics()
    g.fillStyle(0x070a10, 1)
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT)
    const vpX = DESIGN_WIDTH / 2
    const vpY = 470
    const W = DESIGN_WIDTH
    const H = DESIGN_HEIGHT
    // ceiling (two triangles forming a quad to the vanishing point)
    g.fillStyle(0x0c1119, 1)
    g.fillTriangle(0, 0, W, 0, vpX + 150, vpY)
    g.fillTriangle(0, 0, vpX + 150, vpY, vpX - 150, vpY)
    // floor
    g.fillStyle(0x101826, 1)
    g.fillTriangle(0, H, W, H, vpX + 150, vpY)
    g.fillTriangle(0, H, vpX + 150, vpY, vpX - 150, vpY)
    // perspective lines from the four corners to the vanishing point
    g.lineStyle(1, 0x223049, 0.5)
    g.lineBetween(0, 0, vpX, vpY)
    g.lineBetween(W, 0, vpX, vpY)
    g.lineBetween(0, H, vpX, vpY)
    g.lineBetween(W, H, vpX, vpY)
    g.lineStyle(2, 0x2a3850, 0.7)
    g.strokeRect(vpX - 150, vpY - 170, 300, 170)
  }

  private buildUi(target: TargetView): void {
    this.add.text(DESIGN_WIDTH / 2, 40, t('app.title'), { fontFamily: FONT, fontSize: '14px', color: COLORS.sub }).setOrigin(0.5)

    // Small "you" badge (top-left): the player's deployed character. Combat is first-person, so
    // without this you'd never see your own character/cosmetic mid-fight — making the deployed
    // roster identity visible here is the point (docs/playability-monetization.md §2/§5).
    // Texture-guarded: a missing portrait just omits the badge (no crash, no blank box).
    const meKey = porKey(portraitSetFor(getActiveChar()), 'plane')
    if (this.textures.exists(meKey)) {
      this.add.rectangle(58, 80, 76, 76, 0x0b1018, 0.92).setStrokeStyle(2, COLORS.zoneSafe)
      this.add.image(58, 80, meKey).setScale(0.5)
      this.add.text(58, 124, t('combat.you'), { fontFamily: FONT, fontSize: '11px', color: COLORS.sub }).setOrigin(0.5)
    }

    // framed illustrated portrait (heroine set, emotion = combat state)
    this.add.rectangle(DESIGN_WIDTH / 2, 300, 244, 244, 0x0b1018, 0.92).setStrokeStyle(3, ARCH_TINT[target.archetype as keyof typeof ARCH_TINT] ?? 0xffffff)
    this.portrait = this.add.image(DESIGN_WIDTH / 2, 300, porKey(this.portraitSet, 'angry')).setScale(1.7)
    this.portraitHomeX = this.portrait.x
    // One-shot "enemy appears" scale-in on combat start (tween dies with the scene).
    // Skipped under prefers-reduced-motion — the portrait simply appears at rest scale.
    if (!prefersReducedMotion()) {
      this.portrait.setScale(1.45)
      this.tweens.add({ targets: this.portrait, scale: 1.7, duration: 280, ease: 'Back.Out' })
    }

    this.enemyName = this.add.text(DESIGN_WIDTH / 2, 460, '', { fontFamily: FONT, fontSize: '22px', color: COLORS.text, fontStyle: 'bold' }).setOrigin(0.5)
    this.add.rectangle(DESIGN_WIDTH / 2 - 150, 488, 300, 18, COLORS.barBg).setOrigin(0, 0)
    this.enemyHpFill = this.add.rectangle(DESIGN_WIDTH / 2 - 150, 488, 300, 18, COLORS.hp).setOrigin(0, 0)
    this.enemyHpTxt = this.add.text(DESIGN_WIDTH / 2, 497, '', { fontFamily: FONT, fontSize: '12px', color: COLORS.text }).setOrigin(0.5)

    // rage meter (fills toward the special move). The label carries the live numeric
    // value (parity with the HP/SP "N / M" readouts) so the player sees exactly how far
    // from the special they are — it sits above the bar, clear of the centred threshold tick.
    this.rageLabel = this.add.text(DESIGN_WIDTH / 2, 542, t('combat.rage'), { fontFamily: FONT, fontSize: '13px', color: COLORS.sub }).setOrigin(0.5)
    const rageBarLeft = DESIGN_WIDTH / 2 - 110
    const rageBarW = 220
    this.add.rectangle(rageBarLeft, 560, rageBarW, 12, COLORS.barBg).setOrigin(0, 0)
    this.rageFill = this.add.rectangle(rageBarLeft, 560, rageBarW, 12, 0xffa53d).setOrigin(0, 0)
    // Threshold tick: shows exactly how much rage the special needs (fill past it to unleash).
    const threshFrac = Math.max(0, Math.min(1, RAGE_SPECIAL / 100))
    this.add.rectangle(rageBarLeft + rageBarW * threshFrac, 566, 3, 18, 0xfff2cc).setOrigin(0.5)

    // player panel
    this.add.text(24, 705, t('hud.hp'), { fontFamily: FONT, fontSize: '14px', color: COLORS.sub })
    this.add.rectangle(96, 704, 200, 16, COLORS.barBg).setOrigin(0, 0)
    this.hpFill = this.add.rectangle(96, 704, 200, 16, COLORS.hp).setOrigin(0, 0)
    // Numeric HP on the bar (parity with the enemy bar + the SP readout below).
    this.hpValTxt = this.add.text(196, 712, '', { fontFamily: FONT, fontSize: '11px', color: COLORS.text }).setOrigin(0.5)
    this.add.text(24, 730, t('hud.sp'), { fontFamily: FONT, fontSize: '14px', color: COLORS.sub })
    this.add.rectangle(96, 729, 200, 16, COLORS.barBg).setOrigin(0, 0)
    this.spFill = this.add.rectangle(96, 729, 200, 16, COLORS.sp).setOrigin(0, 0)
    // Numeric SP on the bar, mirroring the enemy HP readout (full info parity).
    this.spValTxt = this.add.text(196, 737, '', { fontFamily: FONT, fontSize: '11px', color: COLORS.text }).setOrigin(0.5)
    this.statusTxt = this.add.text(DESIGN_WIDTH - 24, 712, '', { fontFamily: FONT, fontSize: '14px', color: COLORS.text }).setOrigin(1, 0)
    this.injuryTxt = this.add.text(DESIGN_WIDTH - 24, 732, '', { fontFamily: FONT, fontSize: '13px', color: COLORS.bad }).setOrigin(1, 0)

    this.logTxt = this.add.text(24, 760, '', { fontFamily: FONT, fontSize: '13px', color: COLORS.normal, wordWrap: { width: DESIGN_WIDTH - 48, useAdvancedWrap: true }, lineSpacing: 2 }).setOrigin(0, 0)

    this.buildButtons()
  }

  private buildButtons(): void {
    for (const b of this.buttons) b.destroy()
    this.buttons = []
    // Buttons are rebuilt each render; kill any prior pulse before its target dies.
    this.specialTween?.stop()
    this.specialTween = undefined
    if (this.ended) return
    const p = this.player()
    const wep = weaponName(p.weaponName)
    const canSpecial = p.rage >= RAGE_SPECIAL
    // Action buttons are 64px tall (was 58): attack is tapped repeatedly, so it should clear
    // the ~44pt touch floor even after FIT scales the 540-wide canvas down (~0.7× → ~45px) on
    // a phone (parity with the Explore D-pad #213). At y902 they sit between the combat log
    // (ends ~y868) and the canvas bottom (960): top y870, bottom y934 — fits with margin.
    if (canSpecial) {
      this.buttons.push(makeButton(this, 114, 902, { width: 192, height: 64, label: t('action.attackWith', { w: wep }), fontSize: 17, fill: COLORS.btnDanger, onTap: () => this.attack() }))
      const specialBtn = makeButton(this, 318, 902, { width: 152, height: 64, label: t('action.special'), fontSize: 19, fill: 0xc7892a, onTap: () => this.special() })
      this.buttons.push(specialBtn)
      // Subtle "ready to unleash" pulse while the special is available — skipped under
      // prefers-reduced-motion (the button appearing + gold rage bar already signal it).
      if (!prefersReducedMotion()) this.specialTween = this.tweens.add({
        targets: specialBtn,
        scale: { from: 1, to: 1.06 },
        duration: 480,
        yoyo: true,
        repeat: -1,
      })
      this.buttons.push(makeButton(this, 466, 902, { width: 108, height: 64, label: t('action.flee'), fontSize: 16, onTap: () => this.flee() }))
    } else {
      this.buttons.push(makeButton(this, 170, 902, { width: 264, height: 64, label: t('action.attackWith', { w: wep }), fontSize: 22, fill: COLORS.btnDanger, onTap: () => this.attack() }))
      this.buttons.push(makeButton(this, 432, 902, { width: 168, height: 64, label: t('action.flee'), fontSize: 20, onTap: () => this.flee() }))
    }
    this.builtCanSpecial = canSpecial
    this.builtWeapon = p.weaponName
  }

  private render(): void {
    const target = this.target()
    const p = this.player()
    if (!target) return
    this.enemyName.setScale(1) // reset before measuring (a prior long name may have shrunk it)
    // Flag a key-holding enemy with 🔑 — killing them yields the escape key, so the player
    // knows this fight is worth pressing (intel, not a new string; mirrors the HUD key icon).
    const keyMark = target.hasKey ? '🔑 ' : ''
    this.enemyName.setText(`${keyMark}${target.name}　·　${t(ARCH_LABEL[target.archetype as keyof typeof ARCH_LABEL] ?? 'arch.player')}`)
    // Shrink a long localized name·archetype so it never clips off the portrait edges.
    const maxNameW = DESIGN_WIDTH - 32
    if (this.enemyName.width > maxNameW) this.enemyName.setScale(maxNameW / this.enemyName.width)
    const eRatio = Math.max(0, Math.min(1, target.hp / target.mhp))
    // Smoothly drain the enemy HP bar (syncs with the damage float + portrait shake);
    // restart the tween each round so rapid updates never stack.
    this.enemyHpTween?.stop()
    this.enemyHpTween = this.tweens.add({ targets: this.enemyHpFill, scaleX: eRatio, duration: 250, ease: 'Cubic.Out' })
    this.enemyHpFill.setFillStyle(hpColor(eRatio))
    this.enemyHpTxt.setText(`${Math.max(0, target.hp)} / ${target.mhp}`).setColor(hpColorHex(eRatio))
    // "Finish them" pulse when the enemy is critically low (<25%, still alive) —
    // the opportunity mirror of the player's danger pulse (#97). Alpha only.
    // Skip the pulse under prefers-reduced-motion — the low-HP bar colour already
    // conveys "critical"; the alpha pulse is just an urgency enhancement.
    const eCritical = eRatio > 0 && eRatio < 0.25
    if (eCritical && !this.enemyLowTween && !prefersReducedMotion()) {
      this.enemyLowTween = this.tweens.add({ targets: this.enemyHpFill, alpha: { from: 1, to: 0.4 }, duration: 400, yoyo: true, repeat: -1 })
    } else if (!eCritical && this.enemyLowTween) {
      this.enemyLowTween.stop(); this.enemyLowTween = undefined; this.enemyHpFill.setAlpha(1)
    }
    const pRatio = Math.max(0, Math.min(1, p.hp / p.mhp))
    // Smooth player HP drain too (consistency with the enemy bar #93).
    this.playerHpTween?.stop()
    this.playerHpTween = this.tweens.add({ targets: this.hpFill, scaleX: pRatio, duration: 250, ease: 'Cubic.Out' })
    this.hpFill.setFillStyle(hpColor(pRatio))
    this.hpValTxt.setText(`${Math.max(0, p.hp)} / ${p.mhp}`).setColor(hpColorHex(pRatio))
    // Critical-HP danger pulse (alpha) when below 25% — coexists with the scaleX drain
    // (different property). Starts once, stops + resets when no longer critical.
    const critical = pRatio > 0 && pRatio < 0.25
    if (critical && !this.lowHpTween && !prefersReducedMotion()) { // colour already signals critical; pulse is enhancement
      this.lowHpTween = this.tweens.add({ targets: this.hpFill, alpha: { from: 1, to: 0.4 }, duration: 400, yoyo: true, repeat: -1 })
    } else if (!critical && this.lowHpTween) {
      this.lowHpTween.stop(); this.lowHpTween = undefined; this.hpFill.setAlpha(1)
    }
    this.spFill.scaleX = Math.max(0, Math.min(1, p.sp / p.msp))
    this.spValTxt.setText(`${Math.max(0, p.sp)} / ${p.msp}`)
    // Smoothly animate the rage "charge" (consistent with the HP bars #93/#94).
    this.rageTween?.stop()
    this.rageTween = this.tweens.add({ targets: this.rageFill, scaleX: Math.max(0, Math.min(1, p.rage / 100)), duration: 250, ease: 'Cubic.Out' })
    // Bar glows gold once the special is unlocked, echoing the pulsing button.
    this.rageFill.setFillStyle(p.rage >= RAGE_SPECIAL ? 0xffd84d : 0xffa53d)
    // Live numeric rage on the label (parity with HP/SP); gold once the special is ready,
    // muted while charging — a glance tells "how much more rage do I need".
    this.rageLabel.setScale(1) // reset before measuring (a prior longer value may have shrunk it)
    this.rageLabel.setText(`${t('combat.rage')}　${Math.max(0, p.rage)} / 100`).setColor(p.rage >= RAGE_SPECIAL ? '#ffd84d' : COLORS.sub)
    const maxRageW = DESIGN_WIDTH - 32 // fit a wide localized label so it never clips (parity with other lines)
    if (this.rageLabel.width > maxRageW) this.rageLabel.setScale(maxRageW / this.rageLabel.width)
    // Append the round count for a finite-ammo weapon (e.g. "Uzi ×12") so the player can
    // plan around running dry; ≤3 rounds adds a ⚠ "running low" cue (emoji, part of the
    // text → no clash with the weapon-line flash colour). Melee/fists (-1) show no count.
    const ammoTag = p.weaponAmmo >= 0 ? ` ×${p.weaponAmmo}${p.weaponAmmo <= 3 ? ' ⚠' : ''}` : ''
    const wepLabel = weaponName(p.weaponName) + ammoTag
    this.statusTxt.setText(t('status.weapon', { w: wepLabel })) // HP now shows on the bar
    // Persistent injury indicator (chest/head/arm/foot debuffs the player carries).
    const inj = [...p.injuries].sort().map((c) => partLabel(c)).join('・')
    this.injuryTxt.setScale(1) // reset before measuring (a prior longer set may have shrunk it)
    this.injuryTxt.setText(inj ? `🩹 ${inj}` : '')
    // Right-anchored; cap the width so a long all-parts list (e.g. en-US 4 injuries) never
    // reaches across the HUD (parity with the Explore injury indicator #153).
    const maxInjW = 240
    if (this.injuryTxt.width > maxInjW) this.injuryTxt.setScale(maxInjW / this.injuryTxt.width)
    const tail = matchController.getMatch().log.slice(-3).map((l) => l.text)
    this.logTxt.setScale(1) // reset before measuring (prior long lines may have shrunk it)
    this.logTxt.setText(tail.join('\n'))
    // Clamp height so several long wrapped log lines never spill into the action
    // buttons below (the buttons sit at y902; keep the log above ~y868).
    const maxLogH = 868 - 760
    if (this.logTxt.height > maxLogH) this.logTxt.setScale(maxLogH / this.logTxt.height)
  }

  // ─── actions ────────────────────────────────────────────────────────────────

  private attack(): void {
    if (this.ended) return
    this.pendingAttackFx = true // a no-damage result this update reads as a MISS
    gameBus.emit('intent:attack', { targetId: this.targetId })
  }

  private special(): void {
    if (this.ended) return
    // The touch button is only shown when rage is ready, but the desktop 'S' key always
    // reaches here. Only arm the hit/special FX when the move can actually fire: pressing
    // 'S' with too little rage still emits the intent so the controller logs the localized
    // "怒氣不足" reason — but arming pendingAttackFx would also float a misleading "MISS"
    // over the enemy for a move that never connected, so we skip it when not ready.
    if (this.player().rage >= RAGE_SPECIAL) {
      this.pendingSpecialFx = true // the next damage number reads as a special (gold)
      this.pendingAttackFx = true // a whiffed (but fired) special still floats MISS
    }
    gameBus.emit('intent:special', { targetId: this.targetId })
  }

  private flee(): void {
    if (this.ended || this.fleeing) return
    this.fleeing = true // guard against a double-submit while a backend flee is pending
    gameBus.emit('intent:flee', {})
    // Local mode resolves synchronously → end now (unless the flee's world-step ended the
    // match, in which case onOver drives the transition).
    if (!matchController.isBackendMode()) {
      if (matchController.getMatch().status === 'running') this.endCombat()
      return
    }
    // Backend mode is async: the engagement ends when the server clears pending_target_id
    // (onUpdated) or the match ends (onOver). Safety net — if NEITHER arrives (e.g. a network
    // error the controller swallowed), un-latch the guard after a beat so the flee button
    // recovers instead of staying dead. The scene timer is auto-cleared on endCombat/shutdown,
    // so a normal (fast) flee resolves and stops the scene long before this fires.
    this.time.delayedCall(3000, () => { if (!this.ended) this.fleeing = false })
  }

  /** Brief amber scale-pop on the weapon line when the loadout changes (e.g. ammo
   *  depleted → fists). Restores the normal color on completion. Tween dies on shutdown. */
  private flashWeaponLine(): void {
    this.weaponFlashTween?.stop()
    this.statusTxt.setColor('#ffb24d')
    if (prefersReducedMotion()) {
      // No scale pop under reduced-motion — the amber colour conveys the change; restore
      // after a beat via the scene clock (auto-cleared on shutdown).
      this.statusTxt.setScale(1)
      this.time.delayedCall(360, () => this.statusTxt.setColor(COLORS.text))
      return
    }
    this.statusTxt.setScale(1.25)
    this.weaponFlashTween = this.tweens.add({
      targets: this.statusTxt,
      scale: 1,
      duration: 360,
      ease: 'Cubic.Out',
      onComplete: () => this.statusTxt.setColor(COLORS.text),
    })
  }

  // Weapon-type-specific hit effect over the enemy portrait.
  private playHitFx(kind: string): void {
    const x = this.portrait.x
    const y = this.portrait.y
    const g = this.add.graphics().setDepth(25)
    if (kind === 'G') {
      g.lineStyle(4, 0xfff1a8, 1).lineBetween(x - 150, y + 70, x, y)
      g.fillStyle(0xfff1a8, 1).fillCircle(x, y, 10)
    } else if (kind === 'C') {
      g.lineStyle(3, 0x9fe8ff, 0.9).lineBetween(x - 120, y - 90, x + 30, y + 10)
    } else if (kind === 'D') {
      g.fillStyle(0xff7a3d, 0.55).fillCircle(x, y, 90)
    } else {
      g.lineStyle(7, 0xffffff, 0.95).lineBetween(x - 90, y - 80, x + 90, y + 80)
    }
    this.tweens.add({ targets: g, alpha: 0, duration: 300, onComplete: () => g.destroy() })
  }

  // Bigger numbers for bigger hits — crits/specials land heavier, so they read heavier.
  private floatNumber(x: number, y: number, text: string, color: string, dmg = 0): void {
    const size = 26 + Math.min(22, Math.round(dmg / 3))
    const n = this.add.text(x, y, text, { fontFamily: FONT, fontSize: `${size}px`, color, fontStyle: 'bold' }).setOrigin(0.5).setDepth(30)
    // The NUMBER is the information; the upward drift is flourish. Under prefers-reduced-motion
    // hold it in place and fade (still fully readable), otherwise float it up as it fades.
    if (prefersReducedMotion()) {
      this.tweens.add({ targets: n, alpha: 0, duration: 700, delay: 320, onComplete: () => n.destroy() })
    } else {
      this.tweens.add({ targets: n, y: y - 50, alpha: 0, duration: 700, onComplete: () => n.destroy() })
    }
  }

  private baseEmotion(): Emotion {
    const target = this.target()
    if (!target || !target.alive) return 'sad'
    return target.hp / target.mhp < 0.3 ? 'sad' : 'angry'
  }

  private setEmotion(emo: Emotion): void {
    this.portrait.setTexture(porKey(this.portraitSet, emo))
  }

  private onUpdated(): void {
    const target = this.target()
    const p = this.player()
    if (!target) return

    const wasSpecial = this.pendingSpecialFx // consume the flag whether or not damage lands
    this.pendingSpecialFx = false
    const wasAttack = this.pendingAttackFx
    this.pendingAttackFx = false
    const eDmg = this.prevEnemyHp - target.hp
    if (eDmg > 0) {
      audio.play(wasSpecial ? 'reveal' : 'success') // landed hit (special = brighter cue); once per exchange
      this.playHitFx(p.weaponKind)
      this.floatNumber(this.portrait.x, this.portrait.y - 10, `-${eDmg}`, wasSpecial ? '#ffd84d' : COLORS.bad, eDmg)
      this.portrait.setTint(0xff8a8a)
      this.setEmotion('surprised')
      // The red tint + 'surprised' emotion convey the hit as STATE; the sideways shake is
      // decorative motion — skip it under prefers-reduced-motion (parity with the gated
      // camera shake below), still clearing the tint after a beat so it doesn't stick.
      if (prefersReducedMotion()) {
        this.time.delayedCall(160, () => { if (!this.ended) this.portrait.clearTint() })
      } else {
        // Restart the shake from the resting x so rapid back-to-back hits never drift
        // the portrait sideways (cf. the Explore bump-tween fix).
        this.hitTween?.stop()
        this.portrait.x = this.portraitHomeX
        this.hitTween = this.tweens.add({
          targets: this.portrait, x: this.portraitHomeX + 8, duration: 55, yoyo: true, repeat: 2,
          onComplete: () => { this.portrait.x = this.portraitHomeX; this.portrait.clearTint() },
        })
      }
      this.time.delayedCall(420, () => { if (!this.ended) this.setEmotion(this.baseEmotion()) })
    } else {
      // No damage to the enemy. If the player just attacked and the foe still stands, it
      // was a whiff — float a muted "MISS" over the portrait (the log alone is easy to miss).
      if (wasAttack && target.alive) this.floatNumber(this.portrait.x, this.portrait.y - 10, t('combat.miss'), COLORS.sub)
      this.setEmotion(this.baseEmotion())
    }
    const pDmg = this.prevPlayerHp - p.hp
    if (pDmg > 0) {
      audio.play('error') // player took damage — once per exchange
      this.floatNumber(DESIGN_WIDTH / 2, 700, `-${pDmg}`, COLORS.bad, pDmg)
      // Shake harder for bigger hits (capped so it never turns nauseating) — but honor
      // prefers-reduced-motion: the float number still conveys the hit without the shake.
      if (!prefersReducedMotion()) this.cameras.main.shake(120, Math.min(0.012, 0.004 + pDmg * 0.0002))
    }
    this.prevEnemyHp = target.hp
    this.prevPlayerHp = p.hp

    this.render()
    // Flash the weapon line when it actually changes mid-fight — most often a ranged
    // weapon depleting to fists (the player's damage just dropped, easy to miss). Skip
    // the very first build (builtWeapon undefined) so it never flashes on entry.
    const weaponChanged = this.builtWeapon !== undefined && p.weaponName !== this.builtWeapon
    // Only rebuild the action buttons when the layout actually changes (special
    // availability or weapon), so the special pulse doesn't restart every update.
    if ((p.rage >= RAGE_SPECIAL) !== this.builtCanSpecial || p.weaponName !== this.builtWeapon) {
      this.buildButtons()
    }
    if (weaponChanged) this.flashWeaponLine()

    if (!target.alive && p.alive && !this.ended) {
      this.ended = true
      this.setEmotion('sad')
      for (const b of this.buttons) b.destroy()
      this.buttons = []
      // Reuse the name's fit-to-width (the victory line can be long in some locales).
      this.enemyName.setScale(1).setText(t('log.youKilled', { name: target.name }))
      const maxNameW = DESIGN_WIDTH - 32
      if (this.enemyName.width > maxNameW) this.enemyName.setScale(maxNameW / this.enemyName.width)
      // Defeat: fade the portrait out (+ a falling tilt). Under reduced-motion keep the
      // fade — which conveys the kill — but drop the rotation.
      this.tweens.add(prefersReducedMotion()
        ? { targets: this.portrait, alpha: 0.15, duration: 350 }
        : { targets: this.portrait, alpha: 0.15, angle: 12, duration: 350 })
      // After the kill beat: if the sim queued a three-choice run buff (roguelite boon),
      // present it before leaving combat; otherwise return to Explore as before.
      this.time.delayedCall(700, () => {
        const choices = matchController.getPendingBoon()
        if (choices && choices.length) this.showBoonOverlay(choices)
        else this.endCombat()
      })
      return
    }

    // Backend mode: the server cleared the engagement (player fled / target
    // gone) while the target is still alive → leave combat back to Explore.
    if (!this.ended && target.alive && matchController.isBackendMode() && matchController.getPendingTargetId() !== this.targetId) {
      this.endCombat()
    }
  }

  private onOver(): void {
    // Guard first: if the fight is already resolved, do nothing. Crucially, when the player
    // WINS by landing the last kill, onUpdated's victory path has already set `ended` and
    // shown the "you killed X" banner — match:over then fires in the SAME signal() (updated
    // before over). Without this guard we'd paint a "defeat" banner over a victory (and a
    // duplicate match:over would stack a second banner). The lose banner is for a genuine
    // player death, where the victory path never ran (target still alive).
    if (this.ended) return
    // Player died — Explore.onOver drives the Result transition after a ~850ms beat, so
    // the defeat is on screen long enough to read. Show it as a centered red banner over
    // the portrait instead of the tiny right-aligned weapon-line slot.
    this.ended = true
    for (const b of this.buttons) b.destroy()
    this.buttons = []
    this.statusTxt.setText('')
    const lose = this.add.text(DESIGN_WIDTH / 2, 300, t('result.lose'), { fontFamily: FONT, fontSize: '46px', color: COLORS.bad, fontStyle: 'bold' }).setOrigin(0.5)
    const maxW = DESIGN_WIDTH - 48 // shrink a long localized "defeat" line so it never clips
    if (lose.width > maxW) lose.setScale(maxW / lose.width)
  }

  // Three-choice run-buff (boon) modal, shown after a kill. Picking one emits intent:chooseBoon
  // (the sim applies it + clears the offer) and then leaves combat. A modal in-canvas panel (per
  // CLAUDE.md: destructive/blocking actions explain state in-app, not window.confirm); every card
  // shows a localized name + effect so no choice is opaque, and no raw i18n key can surface.
  private showBoonOverlay(choices: string[]): void {
    const cx = DESIGN_WIDTH / 2
    const scrim = this.add.rectangle(cx, DESIGN_HEIGHT / 2, DESIGN_WIDTH, DESIGN_HEIGHT, 0x000000, 0.72).setInteractive()
    const title = this.add.text(cx, DESIGN_HEIGHT * 0.22, t('boon.title'), { fontFamily: FONT, fontSize: '30px', color: COLORS.text, fontStyle: 'bold' }).setOrigin(0.5)
    const objs: Phaser.GameObjects.GameObject[] = [scrim, title]
    const cardH = 96
    const gap = 18
    const top = DESIGN_HEIGHT * 0.32
    choices.forEach((id, i) => {
      const y = top + i * (cardH + gap)
      const card = this.add.rectangle(cx, y, DESIGN_WIDTH - 56, cardH, 0x18222f).setStrokeStyle(2, COLORS.zoneSafe).setInteractive({ useHandCursor: true })
      const name = this.add.text(cx, y - 18, boonName(id), { fontFamily: FONT, fontSize: '22px', color: COLORS.text, fontStyle: 'bold' }).setOrigin(0.5)
      const desc = this.add.text(cx, y + 16, boonDesc(id), { fontFamily: FONT, fontSize: '16px', color: COLORS.sub }).setOrigin(0.5)
      card.on('pointerup', () => {
        audio.play('uiTap')
        gameBus.emit('intent:chooseBoon', { boonId: id })
        for (const o of objs) o.destroy()
        this.endCombat()
      })
      objs.push(card, name, desc)
    })
  }

  private endCombat(): void {
    if (this.scene.isPaused('Explore')) this.scene.resume('Explore')
    this.scene.stop()
  }

  private cleanup(): void {
    for (const dispose of this.disposers) dispose()
    this.disposers = []
    this.specialTween?.stop()
    this.specialTween = undefined
    this.hitTween?.stop()
    this.hitTween = undefined
    this.enemyHpTween?.stop()
    this.enemyHpTween = undefined
    this.playerHpTween?.stop()
    this.playerHpTween = undefined
    this.rageTween?.stop()
    this.rageTween = undefined
    this.lowHpTween?.stop()
    this.lowHpTween = undefined
    this.enemyLowTween?.stop()
    this.enemyLowTween = undefined
    this.weaponFlashTween?.stop()
    this.weaponFlashTween = undefined
  }
}
